/**
 * airan-ntn — NTN-Aware Scheduler
 * UE selection considering elevation, Doppler, feeder link status, satellite energy
 */

import { Scheduler, SchedulerType, SimConfig, UserEquipment, Satellite } from '../types.js';
import { vec3Len, vec3Sub } from '../types.js';
import { calculateElevation } from '../orbital.js';

// ──────────────────────────────────────────────────────────────────────────────
// Standard Schedulers (from Wireless-Decentralized-FL)
// ──────────────────────────────────────────────────────────────────────────────

export class RandomScheduler implements Scheduler {
  readonly name = 'Random' as const;
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);
    const shuffled = [...available].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

export class RoundRobinScheduler implements Scheduler {
  readonly name = 'RoundRobin' as const;
  private pointer = 0;
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0).sort((a, b) => a.id - b.id);
    if (available.length === 0) return [];
    const selected: UserEquipment[] = [];
    for (let i = 0; i < k && selected.length < available.length; i++) {
      const idx = (this.pointer + i) % available.length;
      selected.push(available[idx]);
    }
    this.pointer = (this.pointer + k) % available.length;
    return selected;
  }
  reset(): void { this.pointer = 0; }
}

export class MaxMinScheduler implements Scheduler {
  readonly name = 'MaxMin' as const;
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);
    available.sort((a, b) => b.snr - a.snr);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

export class MaxMaxScheduler implements Scheduler {
  readonly name = 'MaxMax' as const;
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);
    available.sort((a, b) => b.dataSize - a.dataSize);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

export class DCCScheduler implements Scheduler {
  readonly name = 'DCC' as const;
  constructor(private bsCooperation: boolean) { }
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);
    available.sort((a, b) => b.snr - a.snr);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

export class GroverScheduler implements Scheduler {
  readonly name = 'Grover' as const;
  constructor(private snrThreshold: number) { }
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0 && u.snr >= this.snrThreshold);
    available.sort((a, b) => b.snr - a.snr);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

export class SpatialScheduler implements Scheduler {
  readonly name = 'Spatial' as const;
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);
    const selected: UserEquipment[] = [];
    const remaining = [...available].sort(() => Math.random() - 0.5);
    while (selected.length < k && remaining.length > 0) {
      const candidate = remaining.pop()!;
      let minDist = Infinity;
      for (const s of selected) {
        const dist = vec3Len(vec3Sub(candidate.position, s.position));
        if (dist < minDist) minDist = dist;
      }
      if (selected.length === 0 || minDist > 100000) {
        selected.push(candidate);
      } else {
        remaining.push(candidate);
        if (remaining.length === selected.length) break;
      }
    }
    return selected;
  }
  reset(): void {}
}

export class BeamformingScheduler implements Scheduler {
  readonly name = 'Beamforming' as const;
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);
    const bySat = new Map<number, UserEquipment[]>();
    for (const ue of available) {
      if (ue.servingSatId !== null) {
        if (!bySat.has(ue.servingSatId)) bySat.set(ue.servingSatId, []);
        bySat.get(ue.servingSatId)!.push(ue);
      }
    }
    const selected: UserEquipment[] = [];
    for (const [, satUes] of bySat) {
      satUes.sort((a, b) => b.snr - a.snr);
      selected.push(satUes[0]);
      if (selected.length >= k) break;
    }
    return selected.slice(0, k);
  }
  reset(): void {}
}

export class VirtualMIMOScheduler implements Scheduler {
  readonly name = 'VirtualMIMO' as const;
  select(ues: UserEquipment[], k: number): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);
    available.sort((a, b) => b.snr - a.snr);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

// ──────────────────────────────────────────────────────────────────────────────
// NTN-Aware Schedulers
// ──────────────────────────────────────────────────────────────────────────────

export class NtnElevationScheduler implements Scheduler {
  readonly name = 'NtnElevation' as const;

  select(ues: UserEquipment[], k: number, _round: number, satellites?: Satellite[], config?: SimConfig): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);

    // Score by elevation angle (higher = better link quality)
    for (const ue of available) {
      let elevation = 0;
      if (ue.servingSatId !== null && satellites && config) {
        const sat = satellites.find(s => s.id === ue.servingSatId);
        if (sat) {
          elevation = calculateElevation(ue.position, sat.position);
        }
      }
      // Boost SNR by elevation factor
      ue.snr = ue.snr * (1 + elevation / 90);
    }

    available.sort((a, b) => b.snr - a.snr);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

export class NtnDopplerScheduler implements Scheduler {
  readonly name = 'NtnDoppler' as const;

  select(ues: UserEquipment[], k: number, _round: number, _satellites?: Satellite[], config?: SimConfig): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);

    // Penalize high Doppler shift
    for (const ue of available) {
      const dopplerPenalty = Math.min(1, Math.abs(ue.dopplerShift) / (config?.maxDopplerHz ?? 5000));
      ue.snr = ue.snr * (1 - dopplerPenalty * 0.5);
    }

    available.sort((a, b) => b.snr - a.snr);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

export class NtnFeederAwareScheduler implements Scheduler {
  readonly name = 'NtnFeederAware' as const;

  select(ues: UserEquipment[], k: number, _round: number, satellites?: Satellite[], config?: SimConfig): UserEquipment[] {
    const available = ues.filter(u => u.dataSize > 0);

    // Require active feeder link for FL aggregation
    if (config?.requireFeederLink && satellites) {
      const feederActive = new Set<number>();
      for (const sat of satellites) {
        if (sat.feederLink?.active) feederActive.add(sat.id);
      }

      // Filter UEs whose serving satellite has active feeder link
      const withFeeder = available.filter(ue =>
        ue.servingSatId !== null && feederActive.has(ue.servingSatId)
      );

      if (withFeeder.length >= k) {
        withFeeder.sort((a, b) => b.snr - a.snr);
        return withFeeder.slice(0, k);
      }
      // Fallback: mix of feeder and non-feeder
      withFeeder.sort((a, b) => b.snr - a.snr);
      const withoutFeeder = available.filter(ue =>
        ue.servingSatId === null || !feederActive.has(ue.servingSatId!)
      );
      withoutFeeder.sort((a, b) => b.snr - a.snr);
      return [...withFeeder.slice(0, Math.ceil(k / 2)), ...withoutFeeder.slice(0, Math.floor(k / 2))];
    }

    available.sort((a, b) => b.snr - a.snr);
    return available.slice(0, Math.min(k, available.length));
  }
  reset(): void {}
}

// ──────────────────────────────────────────────────────────────────────────────
// Scheduler Factory
// ──────────────────────────────────────────────────────────────────────────────

export function createScheduler(config: SimConfig): Scheduler {
  switch (config.scheduler) {
    case 'Random': return new RandomScheduler();
    case 'RoundRobin': return new RoundRobinScheduler();
    case 'MaxMin': return new MaxMinScheduler();
    case 'MaxMax': return new MaxMaxScheduler();
    case 'DCC': return new DCCScheduler(config.bsCooperation);
    case 'Grover': return new GroverScheduler(config.snrThreshold);
    case 'Spatial': return new SpatialScheduler();
    case 'Beamforming': return new BeamformingScheduler();
    case 'VirtualMIMO': return new VirtualMIMOScheduler();
    case 'NtnElevation': return new NtnElevationScheduler();
    case 'NtnDoppler': return new NtnDopplerScheduler();
    case 'NtnFeederAware': return new NtnFeederAwareScheduler();
    default: return new RandomScheduler();
  }
}

// Helper function to call NTN-aware schedulers with satellite context
export function selectUEs(
  scheduler: Scheduler,
  ues: UserEquipment[],
  k: number,
  round: number,
  satellites?: Satellite[],
  config?: SimConfig
): UserEquipment[] {
  // Use the extended signature if available
  if (scheduler.select.length > 2) {
    return scheduler.select(ues, k, round, satellites, config);
  }
  return scheduler.select(ues, k);
}